import { useMemo } from 'react';
import { Box, Flex, Text, IconButton, Tooltip } from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import Chart from 'react-apexcharts';
import {
  RAG_COLORS,
  formatCurrency,
  formatRatio,
  formatYearMonth,
  type RAGStatus,
} from 'utils/rag';
import { downloadCsvFile, toFileSlug, todayStr } from 'utils/csv';

interface TrendMonth {
  year_month: string;
  score: number;
  broker_score?: number | null;
  quotes: number;
  closing_ratio: number;
  nb_premium_total: number;
  archived: number;
  p2p_percent?: number;
  rag_score: RAGStatus;
  rag_quotes: RAGStatus;
  rag_closing: RAGStatus;
}

interface Props {
  data: TrendMonth[];
  isAdmin?: boolean;
  trendsScope?: string;
}

function downloadCSV(data: TrendMonth[], scope: string) {
  const headers = [
    'Month',
    'Sales Score',
    'Quotes',
    'Closing %',
    'NB Premium',
    'P2P %',
    'Archived',
  ];
  const rows = [...data]
    .reverse()
    .map((m) => [
      m.year_month,
      String(Math.round(m.score)),
      String(m.quotes),
      formatRatio(m.closing_ratio),
      formatCurrency(m.nb_premium_total),
      m.p2p_percent != null ? `${Math.round(m.p2p_percent)}%` : 'N/A',
      String(m.archived),
    ]);
  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  downloadCsvFile(csv, `kpi_trends_r12_${toFileSlug(scope)}_${todayStr()}`);
}

export const TrendsChart = ({ data, isAdmin, trendsScope = 'BIG' }: Props) => {
  // Oldest → newest left to right (API returns newest first)
  const ordered = useMemo(() => [...data].reverse(), [data]);

  // Closing ratio (0-1 decimal) is converted to percentage (×100) to share the same scale.
  const primaryValues = useMemo(() => {
    const vals: number[] = [];
    for (const m of ordered) {
      if (m.score > 0) vals.push(Math.round(m.score));
      if ((m.broker_score ?? 0) > 0) vals.push(Math.round(m.broker_score!));
      if ((m.p2p_percent ?? 0) > 0) vals.push(m.p2p_percent!);
      if (m.closing_ratio > 0) vals.push(Math.round(m.closing_ratio * 100));
    }
    return vals;
  }, [ordered]);

  const yMin = useMemo(() => {
    if (primaryValues.length === 0) return 0;
    return Math.max(0, Math.min(...primaryValues) - 10);
  }, [primaryValues]);

  const yMax = useMemo(() => {
    if (primaryValues.length === 0) return 100;
    return Math.max(...primaryValues) + 10;
  }, [primaryValues]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: 'kpi-r12-trend',
        type: 'line' as const,
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: 'inherit',
      },
      xaxis: {
        categories: ordered.map((m) => formatYearMonth(m.year_month, 'short')),
        labels: { style: { fontSize: '11px', colors: '#718096' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: { text: '' },
        min: yMin,
        max: yMax,
        tickAmount: 5,
        labels: {
          formatter: (v: number) => String(Math.round(v)),
          style: { fontSize: '11px', colors: '#718096' },
        },
      },
      colors: ['#6B48FF', '#38A169', '#DD6B20', '#2B6CB0'],
      stroke: { curve: 'smooth' as const, width: [2, 2, 2, 2] },
      connectNulls: false,
      fill: {
        type: ['gradient', 'solid', 'solid', 'solid'],
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.18,
          opacityTo: 0.01,
          stops: [0, 95],
        },
      },
      dataLabels: { enabled: false },
      markers: {
        size: [4, 4, 4, 4],
        strokeWidth: 0,
        hover: { size: 4, sizeOffset: 0 },
      },
      grid: {
        borderColor: '#EDF2F7',
        strokeDashArray: 3,
        xaxis: { lines: { show: false } },
        padding: { top: 8, left: 8, right: 12, bottom: 4 },
      },
      // RAG threshold benchmark lines — score (80/90), closing % (20), P2P (100)
      annotations: {
        yaxis: [
          {
            y: 90,
            borderColor: RAG_COLORS.green.text,
            borderWidth: 1,
            strokeDashArray: 4,
            label: {
              text: 'Score 90',
              position: 'left',
              offsetX: 4,
              style: {
                background: 'transparent',
                color: RAG_COLORS.green.text,
                fontSize: '9px',
                padding: { left: 2, right: 2, top: 1, bottom: 1 },
              },
            },
          },
          {
            y: 80,
            borderColor: RAG_COLORS.amber.text,
            borderWidth: 1,
            strokeDashArray: 4,
            label: {
              text: 'Score 80',
              position: 'left',
              offsetX: 4,
              style: {
                background: 'transparent',
                color: RAG_COLORS.amber.text,
                fontSize: '9px',
                padding: { left: 2, right: 2, top: 1, bottom: 1 },
              },
            },
          },
          {
            y: 20,
            borderColor: '#805AD5',
            borderWidth: 1,
            strokeDashArray: 6,
            label: {
              text: 'Close 20%',
              position: 'right',
              offsetX: -4,
              style: {
                background: 'transparent',
                color: '#805AD5',
                fontSize: '9px',
                padding: { left: 2, right: 2, top: 1, bottom: 1 },
              },
            },
          },
          {
            y: 100,
            borderColor: '#2F855A',
            borderWidth: 1,
            strokeDashArray: 6,
            label: {
              text: 'P2P 100%',
              position: 'right',
              offsetX: -4,
              style: {
                background: 'transparent',
                color: '#2F855A',
                fontSize: '9px',
                padding: { left: 2, right: 2, top: 1, bottom: 1 },
              },
            },
          },
        ],
      },
      legend: {
        fontSize: '11px',
        position: 'top' as const,
        horizontalAlign: 'right' as const,
      },
      tooltip: {
        y: [
          { formatter: (v: number) => String(Math.round(v)) },
          { formatter: (v: number) => `${Math.round(v)}%` },
          {
            formatter: (v: number | null) =>
              v != null ? `${Math.round(v)}%` : 'N/A',
          },
          { formatter: (v: number) => String(Math.round(v)) },
        ],
      },
    }),
    [ordered, yMin, yMax],
  );

  const hasBrokerScore = ordered.some((m) => m.broker_score != null);

  const series = useMemo(
    () => [
      {
        name: 'Sales Score',
        type: 'area',
        data: ordered.map((m) => Math.round(m.score)),
      },
      {
        name: 'Closing %',
        type: 'line',
        data: ordered.map((m) => Math.round(m.closing_ratio * 100)),
      },
      {
        name: 'P2P %',
        type: 'line',
        data: ordered.map((m) =>
          (m.p2p_percent ?? 0) > 0 ? m.p2p_percent! : null,
        ),
      },
      ...(hasBrokerScore
        ? [
            {
              name: 'Broker Score',
              type: 'line',
              data: ordered.map((m) =>
                m.broker_score != null ? Math.round(m.broker_score) : null,
              ),
            },
          ]
        : []),
    ],
    [ordered, hasBrokerScore],
  );

  if (data.length === 0) {
    return (
      <Box
        textAlign="center"
        py="48px"
        color="gray.400"
        fontSize="sm"
        bg="gray.50"
        borderRadius="12px"
        border="1px dashed"
        borderColor="gray.200"
      >
        {isAdmin
          ? 'Rolling 12-month trend will appear once monthly data is cached. Select a branch to view branch-level R12 trends.'
          : 'No trend data available yet. Trends populate after the first Celery warm-up.'}
      </Box>
    );
  }

  const latest = ordered[ordered.length - 1];

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb="16px">
        <Box>
          <Text fontWeight="700" fontSize="md" color="gray.800">
            Rolling 12 Months — Performance Trend
          </Text>
          <Text fontSize="xs" color="gray.400" mt="2px">
            {trendsScope} · {ordered.length} months ·{' '}
            {formatYearMonth(ordered[0]?.year_month, 'short')} →{' '}
            {formatYearMonth(latest?.year_month, 'short')}
          </Text>
        </Box>
        <Tooltip label="Download CSV">
          <IconButton
            aria-label="Download trends CSV"
            icon={<DownloadIcon />}
            size="sm"
            variant="outline"
            onClick={() => downloadCSV(data, trendsScope)}
          />
        </Tooltip>
      </Flex>

      {/* Chart — CSS-level horizontal scroll only.
           Inner box enforces a minimum width of 72px per data point so labels
           never crowd. On wide screens width="100%" fills the container.
           overflowY="hidden" prevents any vertical scroll from appearing. */}
      <Box
        w="100%"
        overflowX="auto"
        overflowY="hidden"
        sx={{ WebkitOverflowScrolling: 'touch' }}
      >
        <Box minW={`${Math.max(ordered.length * 72, 480)}px`}>
          <Chart
            options={chartOptions}
            series={series}
            type="line"
            height={280}
            width="100%"
          />
        </Box>
      </Box>
    </Box>
  );
};
